import { Form, Formik } from "formik";
import { atom, useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { formDataAtom } from "../App";
import axios from "axios";
import {
  WITH_APP,
  WITH_BLIP,
  WITH_OTHER,
  WITH_TICKET,
  WITH_TRAVELCARD,
} from "./PaymentMethod";
import {
  COMPENSATION_TICKET,
  COMPENSATION_TAXI,
  COMPENSATION_CAR,
} from "./Compensation";
import { VALUE_CODE, BANKDEPOSIT } from "./RefundPreference";
import {
  DELAY_20_39_MINUTES,
  DELAY_40_59_MINUTES,
  DELAY_OVER_60_MINUTES,
} from "./Delay";

import { useRef, useState } from "react";

import useFocusOnLoad from "../hooks/useFocusOnLoad";

import {
  Box,
  Button,
  handleSubmitClick,
  ErrorList,
  Progressbar,
  CheckboxForm,
  Spinner,
  Icon,
  Heading,
} from "@ogt/ui";

import { URLS } from "src/config/urls";

interface SummaryModel {
  gdprAccept: boolean;
}
interface Issue {
  id: string;
}

export const issueAtom = atom<Issue>({
  id: "",
});

export default function Summary() {
  const [formData, setFormData] = useAtom(formDataAtom);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const navigate = useNavigate();
  const progress = 100;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setIssueId] = useAtom(issueAtom);
  const formRef = useRef<HTMLFormElement>(null);

  function GetEditButtonForNavigation(path: string, text?: string) {
    return (
      <Button
        type="button"
        variant="ghost"
        showIcon
        iconName="square-pen"
        iconPosition="left"
        onClick={() => navigate(path)}
      >
        {text ?? "Gå tillbaka och ändra"}
      </Button>
    );
  }

  return (
    <div>
      <Formik<SummaryModel>
        onSubmit={(values) => {

          if (isFormSubmitted) {
            return;
          }

          setIsFormSubmitted(true);

          const emailContact = formData.emailcontact;
          const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

          window.scrollTo({
            top: 0,
            behavior: "smooth", // Smooth scrolling
          });


          const payload = {
            Traveller: {
              Firstname: formData.firstname,
              Lastname: formData.lastname,
              Address: formData.streetaddress,
              ZipCode: formData.zipcode,
              City: formData.postaladdress,
              Phone: formData.phonenumbercontact,
              Email: formData.emailcontact,
            },

            Journey: {
              FromName: formData.selectedJourney.fromName,
              ToName: formData.selectedJourney.toName,
              LineNames: [formData.selectedJourney.line],
              Switches: Number(formData.selectedJourney.switches),
              Departure: formData.selectedJourney.date,
            },

            CompensationType: formData.compensation,

            JourneyDelay: formData.delay,

            TicketInfo: {
              TicketType: formData.paymentmethod,
              TicketId: formData.ticketnumber,
              TravelCardNumber: formData.cardnumber,
              PhoneNumber: formData.phonenumberapp,
            },

            CarTrip: {
              From: formData.cartravelfrom,
              To: formData.cartravelto,
              Distance: Number(formData.distance),
              RegistrationNumber: formData.carlicenceplate,
              Expenses: formData.carexpenses,
            },

            CompensationPaymentMethod: formData.refundpreference,

            BankDetails: {
              ClearingNumber: formData.clearingnumber,
              AccountNumber: formData.accountnumber,
            },

            TaxiReceipts: formData.taxireceipt,

            ValueCodeEmail: formData.valuecodeemail,

            OtherInfo: formData.moreinfo,
          };

          axios
            .post(
              `${apiBaseUrl?.replace(/\/$/, "") ?? ""}/api/v1/Crm/DelayCompensationApplication`,
              payload,
              {
                headers: {
                  "referrer-policy": "strict-origin",
                },
              }
            )
            .then((result) => {
              setIssueId({ id: result.data });
              setFormData({
                paymentmethod: "",
                phonenumberapp: "",
                ticketnumber: "",
                cardnumber: "",
                delay: "",
                compensation: "",
                refundpreference: "",
                taxireceipt: [],
                distance: "",
                cartravelfrom: "",
                cartravelto: "",
                carlicenceplate: "",
                carexpenses: [],
                valuecodeemail: "",
                clearingnumber: "",
                accountnumber: "",
                firstname: "",
                lastname: "",
                streetaddress: "",
                zipcode: "",
                postaladdress: "",
                phonenumbercontact: "",
                emailcontact: "",
                moreinfo: "",
                journey: "",
                selectedJourney: {
                  fromName: "",
                  toName: "",
                  date: "",
                  travelTime: "",
                  journeyKey: "",
                  line: "",
                  switches: "",
                  unformattedDate: "",
                },
              });

              navigate("/tack-sida", { state: { emailContact } });
            })
            .catch((error) => {
              navigate("../ett-fel-intraffade");
            });
        }}
        initialValues={{
          gdprAccept: false,
        }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          gdprAccept: Yup.bool().test(
            "checkbox-selected",
            "Du måste godkänna hanteringen av dina personuppgifter.",
            (value) => {
              return value === true;
            }
          ),
          // ...formValidation(formData),
        })}
        enableReinitialize
      >
        {({ errors, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="w-full max-w-xs mb-4">
                <Progressbar percentage={progress} />
              </div>
            </div>
            <Form>
              <Box fullwidth>
                {isFormSubmitted && <Spinner label="Skickar ..." />}
                <div
                  className={`inner-summary ${isFormSubmitted === true ? "hide" : ""
                    }`}
                >
                  <Heading progress={progress} headingRef={h2Ref} focusable>
                    Granska och skicka in ansökan
                  </Heading>

                  {Object.keys(errors).length > 0 && (
                    <ErrorList errors={errors} />
                  )}

                  <p>
                    Vänligen granska uppgifterna innan du skickar in ansökan.
                    Vill du ändra något klickar du på "Gå tillbaka och ändra".
                    När du är färdig klickar du på ”Skicka ansökan”.
                  </p>

                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Hur såg din planerade resa ut?
                    </Heading>

                    <div className="summary-data-wrapper flex flex-col gap-y-2 md:flex-row">
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <span className="summary-data-label font-bold">
                            Från:
                          </span>
                          <span className="summary-data">
                            {formData.selectedJourney.fromName}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="summary-data-label font-bold">
                            Till:
                          </span>
                          <span className="summary-data">
                            {formData.selectedJourney.toName}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="summary-data-label font-bold last">
                            Datum för resa:
                          </span>
                          <span className="summary-data last">
                            {formData.selectedJourney.date}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="summary-data-label font-bold">
                            Avgång- och ankomsttid:
                          </span>
                          <span className="summary-data">
                            {formData.selectedJourney.travelTime}
                          </span>
                        </div>
                      </div>

                      <div className="summary-edit md:ml-auto mt-1">
                        {GetEditButtonForNavigation("../hur-du-reste")}
                      </div>
                    </div>
                  </div>
                  <hr></hr>
                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Hur stor blev förseningen?
                    </Heading>

                    <div className="summary-data-wrapper flex flex-col gap-y-2 md:flex-row">
                      <div className="">
                        <span className="summary-data last">
                          Jag blev {GetDelayReadable(formData.delay)}
                        </span>
                      </div>
                      <div className="summary-edit md:ml-auto mt-1">
                        {GetEditButtonForNavigation("../hur-sen-blev-du")}
                      </div>
                    </div>
                  </div>
                  <hr></hr>
                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Var hade du din biljett?
                    </Heading>
                    <div className="summary-data-wrapper flex flex-col gap-y-2 md:flex-row">
                      <div className="">
                        <span className="summary-data last">
                          {GetWayOfTravelReadable(formData.paymentmethod)}
                        </span>
                      </div>
                      <div className="summary-edit md:ml-auto mt-1">
                        {GetEditButtonForNavigation("../betalningssatt")}
                      </div>
                    </div>
                  </div>
                  <hr></hr>
                  {formData.paymentmethod === WITH_APP ||
                    formData.paymentmethod === WITH_TRAVELCARD ||
                    formData.paymentmethod === WITH_BLIP ||
                    formData.paymentmethod === WITH_TICKET ? (
                    <div className="summary-section patient-summary mb-4">
                      <Heading as="h3" className="summary-section-heading">
                        Biljettinformation
                      </Heading>
                      <div className="summary-data-wrapper flex flex-col gap-y-2 md:flex-row">
                        <div className="">
                          {formData.paymentmethod === WITH_APP ? (
                            <>
                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold">
                                  Telefonnummer:
                                </span>
                                <span className="summary-data">
                                  {formData.phonenumberapp}
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold last">
                                  Biljett-ID:
                                </span>
                                <span className="summary-data last">
                                  {formData.ticketnumber}
                                </span>
                              </div>
                            </>
                          ) : formData.paymentmethod === WITH_TRAVELCARD ? (
                            <>
                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold last">
                                  Reskortsnummer:
                                </span>
                                <span className="summary-data last">
                                  {formData.cardnumber}
                                </span>
                              </div>
                            </>
                          ) : formData.paymentmethod === WITH_BLIP ||
                            formData.paymentmethod === WITH_TICKET ? (
                            <>
                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold last">
                                  Biljett-ID:
                                </span>
                                <span className="summary-data last">
                                  {formData.ticketnumber}
                                </span>
                              </div>
                            </>
                          ) : null}
                        </div>

                        <div className="summary-edit md:ml-auto mt-1">
                          {formData.paymentmethod === WITH_APP ? (
                            <>{GetEditButtonForNavigation("../med-app")}</>
                          ) : formData.paymentmethod === WITH_TRAVELCARD ? (
                            <>{GetEditButtonForNavigation("../med-resekort")}</>
                          ) : formData.paymentmethod === WITH_BLIP ? (
                            <>{GetEditButtonForNavigation("../med-blipp")}</>
                          ) : formData.paymentmethod === WITH_TICKET ? (
                            <>{GetEditButtonForNavigation("../med-biljett")}</>
                          ) : null}
                        </div>
                      </div>
                      <hr></hr>
                    </div>
                  ) : null}

                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Vad önskar du ersättning för?
                    </Heading>
                    <div className="summary-data-wrapper flex flex-col gap-y-2 md:flex-row">
                      <div className="">
                        <span className="summary-data last">
                          Jag vill ha ersättning{" "}
                          {GetCompensationReadable(formData.compensation)}
                        </span>
                      </div>

                      <div className="summary-edit md:ml-auto mt-1">
                        {GetEditButtonForNavigation("../ersattning-for")}
                      </div>
                    </div>
                  </div>
                  <hr></hr>
                  {formData.compensation === COMPENSATION_TAXI ||
                    formData.compensation === COMPENSATION_CAR ? (
                    <div className="summary-section patient-summary mb-4">
                      <Heading as="h3" className="summary-section-heading">
                        Uppgifter om ersättning
                      </Heading>

                      <div className="summary-data-wrapper flex flex-col gap-y-2 md:flex-row">
                        <div>
                          {formData.compensation === COMPENSATION_TAXI ? (
                            <>
                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold last">
                                  Bifogat kvitto:
                                </span>
                                <span className="summary-data last">
                                  {formData.taxireceipt.map((doc) => (
                                    <div key={doc.file.name}>
                                      {doc.file.name}
                                    </div>
                                  ))}
                                </span>
                              </div>
                            </>
                          ) : formData.compensation === COMPENSATION_CAR ? (
                            <>
                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold">
                                  Åkte från:
                                </span>
                                <span className="summary-data">
                                  {formData.cartravelfrom}
                                </span>
                              </div>

                              <div className="flex gap-2">
                                <span
                                  className={`summary-data-label font-bold ${formData.carexpenses.length < 1
                                    ? "last"
                                    : ""
                                    }`}
                                >
                                  Åkte till:
                                </span>
                                <span
                                  className={`summary-data ${formData.carexpenses.length < 1
                                    ? "last"
                                    : ""
                                    }`}
                                >
                                  {formData.cartravelto}
                                </span>
                              </div>

                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold">
                                  Antal mil:
                                </span>
                                <span className="summary-data">
                                  {formData.distance}
                                </span>
                              </div>

                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold">
                                  Registreringsnummer:
                                </span>
                                <span className="summary-data uppercase">
                                  {formData.carlicenceplate}
                                </span>
                              </div>

                              {formData.carexpenses.length > 0 && (
                                <>
                                  <div className="flex gap-2">
                                    <span className="summary-data-label font-bold last">
                                      Bifogat kvitto:
                                    </span>
                                    <span className="summary-data last">
                                      {formData.carexpenses.map((doc) => (
                                        <div key={doc.file.name}>
                                          {doc.file.name}{" "}
                                        </div>
                                      ))}
                                    </span>
                                  </div>
                                </>
                              )}
                            </>
                          ) : null}
                        </div>
                        <div className="summary-edit md:ml-auto mt-1">
                          {formData.compensation === COMPENSATION_TAXI ? (
                            <>
                              {GetEditButtonForNavigation(
                                "../ersattning-for-taxi"
                              )}
                            </>
                          ) : formData.compensation === COMPENSATION_CAR ? (
                            <>
                              {GetEditButtonForNavigation(
                                "../ersattning-for-bil"
                              )}
                            </>
                          ) : null}
                        </div>
                      </div>
                      <hr></hr>
                    </div>
                  ) : null}

                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Hur vill du ha ersättningen utbetald?
                    </Heading>
                    <div className="summary-data-wrapper flex flex-col gap-y-2 md:flex-row">
                      <div className="">
                        <span className="summary-data last">
                          {GetRefundPreference(formData.refundpreference)}
                        </span>
                      </div>
                      <div className="summary-edit md:ml-auto mt-1">
                        {GetEditButtonForNavigation(
                          "../hur-vill-du-ha-ersattning"
                        )}
                      </div>
                    </div>
                  </div>
                  <hr></hr>
                  {formData.refundpreference === VALUE_CODE ||
                    formData.refundpreference === BANKDEPOSIT ? (
                    <div className="summary-section patient-summary mb-4">
                      {formData.refundpreference === VALUE_CODE ? (
                        <>
                          <Heading as="h3" className="summary-section-heading">
                            Värdekod
                          </Heading>
                          <div className="summary-data-wrapper flex flex-col gap-y-2 md:flex-row">
                            <div>
                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold last">
                                  Mejladress för värdekod:
                                </span>
                                <span className="summary-data last">
                                  {formData.valuecodeemail}
                                </span>
                              </div>
                            </div>
                            <div className="summary-edit md:ml-auto mt-1">
                              {GetEditButtonForNavigation("../vardekod")}
                            </div>
                          </div>
                        </>
                      ) : formData.refundpreference === BANKDEPOSIT ? (
                        <>
                          <Heading as="h3" className="summary-section-heading">
                            Insättning på bankkonto
                          </Heading>
                          <div className="summary-data-wrapper flex flex-col gap-y-2 md:flex-row">
                            <div>
                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold">
                                  Clearingnummer:
                                </span>
                                <span className="summary-data">
                                  {formData.clearingnumber}
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold last">
                                  Kontonummer:
                                </span>
                                <span className="summary-data last">
                                  {formData.accountnumber}
                                </span>
                              </div>
                            </div>
                            <div className="summary-edit md:ml-auto mt-1">
                              {GetEditButtonForNavigation("../bankinsattning")}
                            </div>
                          </div>
                        </>
                      ) : null}
                      <hr></hr>
                    </div>
                  ) : null}

                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Personuppgifter
                    </Heading>
                    <div className="summary-data-wrapper flex flex-col gap-y-2 md:flex-row">
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <span className="summary-data-label font-bold">
                            Förnamn:
                          </span>
                          <span className="summary-data">
                            {formData.firstname}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="summary-data-label font-bold">
                            Efternamn:
                          </span>
                          <span className="summary-data">
                            {formData.lastname}
                          </span>
                        </div>

                        <div className="flex gap-2">
                          <span className="summary-data-label font-bold">
                            Gatuadress:
                          </span>
                          <span className="summary-data">
                            {formData.streetaddress}
                          </span>
                        </div>

                        <div className="flex gap-2">
                          <span className="summary-data-label font-bold">
                            Postnummer:
                          </span>
                          <span className="summary-data">
                            {formData.zipcode}
                          </span>
                        </div>

                        <div className="flex gap-2">
                          <span className="summary-data-label font-bold">
                            Postort:
                          </span>
                          <span className="summary-data">
                            {formData.postaladdress}
                          </span>
                        </div>

                        <div className="flex gap-2">
                          <span className="summary-data-label font-bold">
                            Telefonnummer för kontakt:
                          </span>
                          <span className="summary-data">
                            {formData.phonenumbercontact}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="summary-data-label font-bold">
                            Mejladress för kontakt:
                          </span>
                          <span className="summary-data">
                            {formData.emailcontact}
                          </span>
                        </div>
                        {formData.moreinfo && (
                          <div className="flex gap-2">
                            <span className="summary-data-label font-bold last">
                              Information om resan:
                            </span>
                            <span className="summary-data last">
                              {formData.moreinfo}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="summary-edit md:ml-auto mt-1">
                        {GetEditButtonForNavigation("../personuppgifter")}
                      </div>
                    </div>
                  </div>
                  <hr></hr>
                  <div className="mt-6 mb-6 md:mb-8">
                    <fieldset className="border-0 p-0 m-0">
                      <legend className="sr-only">
                        Godkännande av personuppgifter
                      </legend>
                      <CheckboxForm width="fullWidth" name="gdprAccept">
                        Jag godkänner att Östgötatrafiken sparar och hanterar
                        mina personuppgifter enligt{" "}
                        <a
                          className="flex"
                          target="_blank"
                          rel="noreferrer"
                          href={URLS.privacyPolicy}
                        >
                          relevant dataskyddslagstiftning.
                          <Icon
                            className="inline-block ml-1"
                            name="external-link"
                            size={12}
                          />
                        </a>
                      </CheckboxForm>
                    </fieldset>
                  </div>
                </div>
              </Box>

              <div
                className={`flex justify-centery ${isFormSubmitted === true ? "hide" : ""
                  }`}
              >
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button
                    type="submit"
                    disabled={isFormSubmitted}
                    onClick={handleSubmitClick(formRef)}
                  >
                    {isFormSubmitted ? (
                      <>
                        <Icon
                          name="loader-circle"
                          className="inline-block size-5 animate-spin"
                          aria-hidden="true"
                        />
                        Skickar...
                      </>
                    ) : (
                      "Skicka ansökan"
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}

function GetWayOfTravelReadable(PaymentMethod: string): string {
  return PaymentMethod === WITH_APP
    ? "I appen"
    : PaymentMethod === WITH_TRAVELCARD
      ? "På ett reskort"
      : PaymentMethod === WITH_BLIP
        ? "Blippat bankkort ombord"
        : PaymentMethod === WITH_TICKET
          ? "Pappersbiljett"
          : PaymentMethod === WITH_OTHER
            ? "Jag hade ingen biljett"
            : "";
}

function GetCompensationReadable(compensation: string): string {
  return compensation === COMPENSATION_TICKET
    ? "av priset för en enkelbiljett"
    : compensation === COMPENSATION_TAXI
      ? "för resa med taxi"
      : compensation === COMPENSATION_CAR
        ? "för resa med bil"
        : "";
}

function GetRefundPreference(refundpreference: string): string {
  return refundpreference === VALUE_CODE
    ? "Jag vill ha ersättning som en värdekod"
    : refundpreference === BANKDEPOSIT
      ? "Jag vill ha ersättning till mitt bankkonto"
      : "";
}

function GetDelayReadable(delay: string): string {
  return delay === DELAY_20_39_MINUTES
    ? "20 - 39 minuter försenad"
    : delay === DELAY_40_59_MINUTES
      ? "40 - 59 minuter försenad"
      : delay === DELAY_OVER_60_MINUTES
        ? "mer än 60 minuter försenad"
        : "";
}
